import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../shared/auth/useCompanyWriteAccess';
import ifrsService from '../../services/ifrs';
import { fetchActiveIfrsStandards } from '../../services/ifrsStandards';
import './IfrsWorkspacePage.css';

const REPORT_KEYS = ['financialPosition', 'profitLossOci', 'cashFlow', 'equityChanges', 'notes'];

const defaultRuleForm = {
  accountCodeOrPrefix: '',
  classification: 'OPERATING',
  nonCashFlag: false,
  rulePriority: 100,
  effectiveFrom: '',
  effectiveTo: '',
  active: true
};

const mapStatus = (status, isTr) => {
  const key = String(status || '').toUpperCase();
  if (!isTr) return key || '-';
  const map = { RUNNING: 'CALISIYOR', SUCCESS: 'BASARILI', FAILED: 'BASARISIZ', APPROVED: 'ONAYLI', NOT_IMPLEMENTED: 'UYGULANMADI', READY: 'HAZIR', PENDING: 'BEKLIYOR', CANCELLED: 'IPTAL' };
  return map[key] || key || '-';
};

function IfrsWorkspacePage() {
  const { language } = useLanguage();
  const isTr = !String(language || '').toLowerCase().startsWith('en');
  const L = useCallback((en, tr) => (isTr ? tr : en), [isTr]);
  const reportOptions = useMemo(() => ([
    { key: REPORT_KEYS[0], label: L('Financial Position', 'Finansal Durum Tablosu') },
    { key: REPORT_KEYS[1], label: L('Profit or Loss + OCI', 'Kar veya Zarar + OCI') },
    { key: REPORT_KEYS[2], label: L('Cash Flow', 'Nakit Akis Tablosu') },
    { key: REPORT_KEYS[3], label: L('Equity Changes', 'Ozkaynak Degisim Tablosu') },
    { key: REPORT_KEYS[4], label: L('Notes', 'Dipnotlar') }
  ]), [L]);

  const navigate = useNavigate();

  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);

  const [reportType, setReportType] = useState(REPORT_KEYS[0]);
  const [reportResponse, setReportResponse] = useState(null);
  const [allReports, setAllReports] = useState({});
  const [trialBalanceResponse, setTrialBalanceResponse] = useState(null);
  const [reconciliationResponse, setReconciliationResponse] = useState(null);
  const [xbrlResponse, setXbrlResponse] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [rules, setRules] = useState([]);
  const [includeDefaults, setIncludeDefaults] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [ruleForm, setRuleForm] = useState(defaultRuleForm);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);

  const [standardsByCode, setStandardsByCode] = useState({});

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const companyId = selection.companyId;
  const periodId = selection.periodId;
  const hasSelection = Boolean(companyId && periodId);
  const { canWrite } = useCompanyWriteAccess(companyId);
  const canManageRules = canWrite;

  const clearFeedback = useCallback(() => {
    setMessage('');
    setError('');
  }, []);

  useEffect(() => {
    fetchActiveIfrsStandards()
      .then(data => {
        const map = {};
        (data || []).forEach(s => { map[s.standardCode] = s; });
        setStandardsByCode(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadBatchOptions = async () => {
      setBatchOptions([]);
      setSelectedBatchId('');
      setActiveBatch(null);

      if (!hasSelection) return;

      setBatchLoading(true);
      try {
        const data = await ifrsService.listIfrsBatches({ companyId, periodId });
        if (cancelled) return;
        setBatchOptions(Array.isArray(data) ? data : []);
      } catch (caught) {
        if (cancelled) return;
        setError(caught.message || L('Batch list could not be loaded.', 'Islem grubu listesi yuklenemedi.'));
      } finally {
        if (!cancelled) {
          setBatchLoading(false);
        }
      }
    };

    loadBatchOptions();

    return () => {
      cancelled = true;
    };
  }, [L, companyId, hasSelection, periodId]);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(isTr ? 'tr-TR' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [isTr]
  );

  const formatAmount = useCallback((value) => {
    const n = Number(value);
    if (Number.isFinite(n)) return numberFormatter.format(n);
    return String(value ?? '-');
  }, [numberFormatter]);

  const mapSourceTrace = useCallback((trace) => {
    if (!isTr) return trace;
    const map = {
      ifrs_trial_balance_materialized: 'IFRS mizan (materyalize)',
      ifrs_trial_balance: 'IFRS mizan',
      vuk_trial_balance: 'VUK mizan',
      manual_entry: 'manuel giris',
      manual: 'manuel',
      transformation_rule: 'donusum kurali',
      adjustment: 'duzeltme',
      opening_balance: 'acilis bakiyesi',
      system_generated: 'sistem uretimi',
      cashflow_classification_rules: 'nakit akis siniflandirma kurallari'
    };
    const key = String(trace || '').toLowerCase().trim();
    return map[key] || trace;
  }, [isTr]);

  const mapAccountType = useCallback((type) => {
    if (!isTr) return type;
    const map = {
      ASSET: 'VARLIK', LIABILITY: 'YUKUMLULUK', EQUITY: 'OZKAYNAK',
      INCOME: 'GELIR', EXPENSE: 'GIDER', OCI: 'DIGER KAPSAMLI GELIR',
      REVENUE: 'HASILAT', COST: 'MALIYET', TAX: 'VERGI', UNKNOWN: 'BILINMEYEN',
      OPERATING: 'ISLETME', INVESTING: 'YATIRIM', FINANCING: 'FINANSMAN',
      UNCLASSIFIED: 'SINIFLANDIRILMAMIS', NON_CASH: 'NAKIT_DISI'
    };
    return map[String(type || '').toUpperCase()] || type;
  }, [isTr]);

  const mapLineCode = useCallback((code) => {
    if (!isTr) return code;
    const map = {
      OPERATING: 'ISLETME', INVESTING: 'YATIRIM', FINANCING: 'FINANSMAN',
      UNCLASSIFIED: 'SINIFLANDIRILMAMIS',
      OPERATING_NON_CASH: 'ISLETME_NAKIT_DISI',
      INVESTING_NON_CASH: 'YATIRIM_NAKIT_DISI',
      FINANCING_NON_CASH: 'FINANSMAN_NAKIT_DISI'
    };
    return map[String(code || '').toUpperCase()] || code;
  }, [isTr]);

  const mapLineName = useCallback((name) => {
    if (!isTr) return name;
    const map = {
      'Operating Cash Flow': 'Isletme Nakit Akisi',
      'Investing Cash Flow': 'Yatirim Nakit Akisi',
      'Financing Cash Flow': 'Finansman Nakit Akisi',
      'Unclassified Cash Flow': 'Siniflandirilmamis Nakit Akisi',
      'Operating Non-Cash Adjustments': 'Isletme Nakit-Disi Duzeltmeleri',
      'Investing Non-Cash Adjustments': 'Yatirim Nakit-Disi Duzeltmeleri',
      'Financing Non-Cash Adjustments': 'Finansman Nakit-Disi Duzeltmeleri'
    };
    return map[String(name || '')] || name;
  }, [isTr]);

  const mapCashflowClassificationLabel = useCallback((classification) => {
    const key = String(classification || '').toUpperCase();
    if (!isTr) return key || '-';
    const map = {
      OPERATING: 'İşletme Faaliyetleri',
      INVESTING: 'Yatırım Faaliyetleri',
      FINANCING: 'Finansman Faaliyetleri'
    };
    return map[key] || key || '-';
  }, [isTr]);

  const normalizeRulePayload = useCallback((formState) => ({
    accountCodeOrPrefix: formState.accountCodeOrPrefix.trim(),
    classification: formState.classification,
    nonCashFlag: Boolean(formState.nonCashFlag),
    rulePriority: Number(formState.rulePriority) || 100,
    effectiveFrom: formState.effectiveFrom || null,
    effectiveTo: formState.effectiveTo || null,
    active: Boolean(formState.active)
  }), []);

  const loadBatchById = useCallback(async (batchIdValue) => {
    const parsedBatchId = Number(batchIdValue);
    if (!hasSelection || !parsedBatchId) return;
    clearFeedback();
    setBatchLoading(true);
    try {
      const data = await ifrsService.getIfrsBatch({ batchId: parsedBatchId, companyId, periodId });
      setActiveBatch(data);
      setMessage(L(`Batch loaded: #${data.batchId} (${mapStatus(data.status, false)})`, `İşlem grubu yüklendi: #${data.batchId} (${mapStatus(data.status, true)})`));
    } catch (caught) {
      setError(caught.message || L('Batch detail could not be loaded.', 'İşlem grubu detayı yüklenemedi.'));
    } finally {
      setBatchLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId]);

  const selectedBatchOption = useMemo(
    () => batchOptions.find((item) => String(item.batchId) === String(selectedBatchId)) || null,
    [batchOptions, selectedBatchId]
  );

  const handleApproveBatch = useCallback(async () => {
    if (!canWrite) {
      setError(L('You do not have write access for this company.', 'Bu sirket icin yazma yetkiniz yok.'));
      return;
    }
    if (!hasSelection || !activeBatch?.batchId) return;
    clearFeedback();
    setBatchLoading(true);
    try {
      const data = await ifrsService.approveIfrsBatch({ batchId: activeBatch.batchId, companyId, periodId });
      setActiveBatch(data);
      setMessage(data.message || L(`Batch approved: #${data.batchId}`, `İşlem grubu onaylandı: #${data.batchId}`));
    } catch (caught) {
      setError(caught.message || L('Batch approval failed.', 'İşlem grubu onaylanamadı.'));
    } finally {
      setBatchLoading(false);
    }
  }, [L, activeBatch?.batchId, canWrite, clearFeedback, companyId, hasSelection, periodId]);

  const handleLoadTrialBalance = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setReportLoading(true);
    try {
      const data = await ifrsService.fetchIfrsTrialBalance({ companyId, periodId });
      setTrialBalanceResponse(data);
      setMessage(L(`Trial balance loaded. Rows: ${data?.lines?.length || 0}`, `Trial balance yuklendi. Satir: ${data?.lines?.length || 0}`));
    } catch (caught) {
      setError(caught.message || L('Trial balance could not be loaded.', 'Trial balance yuklenemedi.'));
    } finally {
      setReportLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId]);

  const handleLoadReport = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setReportLoading(true);
    try {
      const data = await ifrsService.fetchIfrsReport({ companyId, periodId, reportType });
      setReportResponse(data);
      setMessage(L(`${data?.reportName || 'Report'} loaded. Rows: ${data?.lines?.length || 0}`, `${data?.reportName || 'Rapor'} yuklendi. Satir: ${data?.lines?.length || 0}`));
    } catch (caught) {
      setError(caught.message || L('IFRS report could not be loaded.', 'IFRS raporu yuklenemedi.'));
    } finally {
      setReportLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId, reportType]);

  const handleLoadAllReports = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setReportLoading(true);
    try {
      const responses = await Promise.all(
        REPORT_KEYS.map(async (key) => [key, await ifrsService.fetchIfrsReport({ companyId, periodId, reportType: key })])
      );
      setAllReports(Object.fromEntries(responses));
      setMessage(L('All IFRS financial statements loaded.', 'Tum IFRS mali tablolari yuklendi.'));
    } catch (caught) {
      setError(caught.message || L('IFRS financial statements could not be loaded.', 'IFRS mali tablolari yuklenemedi.'));
    } finally {
      setReportLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId]);

  const handleLoadReconciliation = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setReportLoading(true);
    try {
      const data = await ifrsService.fetchIfrsReconciliation({ companyId, periodId });
      setReconciliationResponse(data);
      setMessage(L('VUK-IFRS reconciliation loaded.', 'VUK-IFRS mutabakat yuklendi.'));
    } catch (caught) {
      setError(caught.message || L('Reconciliation could not be loaded.', 'Mutabakat yuklenemedi.'));
    } finally {
      setReportLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId]);

  const handleLoadXbrl = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setReportLoading(true);
    try {
      const data = await ifrsService.fetchIfrsXbrlExport({ companyId, periodId });
      setXbrlResponse(data);
      setMessage(L(`XBRL endpoint response: ${mapStatus(data.status, false)}`, `XBRL endpoint cevabi: ${mapStatus(data.status, true)}`));
    } catch (caught) {
      setError(caught.message || L('XBRL endpoint request failed.', 'XBRL endpoint cagrisi basarisiz.'));
    } finally {
      setReportLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, periodId]);

  const loadRules = useCallback(async () => {
    if (!hasSelection) return;
    clearFeedback();
    setRulesLoading(true);
    try {
      const data = await ifrsService.listCashflowRules({ companyId, includeDefaults, includeInactive });
      setRules(data || []);
    } catch (caught) {
      setError(caught.message || L('Cash flow rules could not be loaded.', 'Cash flow kurallari yuklenemedi.'));
    } finally {
      setRulesLoading(false);
    }
  }, [L, clearFeedback, companyId, hasSelection, includeDefaults, includeInactive]);

  const handleSaveRule = useCallback(async () => {
    if (!hasSelection || !canManageRules) return;
    clearFeedback();
    setRulesLoading(true);
    try {
      const payload = normalizeRulePayload(ruleForm);
      if (editingRuleId) {
        await ifrsService.updateCashflowRule({ companyId, ruleId: editingRuleId, payload });
      } else {
        await ifrsService.createCashflowRule({ companyId, payload });
      }
      setRuleForm(defaultRuleForm);
      setEditingRuleId(null);
      await loadRules();
      setMessage(L(editingRuleId ? 'Rule updated.' : 'Rule created.', editingRuleId ? 'Kural guncellendi.' : 'Kural olusturuldu.'));
    } catch (caught) {
      setError(caught.message || L('Rule could not be saved.', 'Kural kaydedilemedi.'));
      setRulesLoading(false);
    }
  }, [L, canManageRules, clearFeedback, companyId, editingRuleId, hasSelection, loadRules, normalizeRulePayload, ruleForm]);

  const handleEditRule = useCallback((rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      accountCodeOrPrefix: rule.accountCodeOrPrefix || '',
      classification: rule.classification || 'OPERATING',
      nonCashFlag: Boolean(rule.nonCashFlag),
      rulePriority: Number(rule.rulePriority) || 100,
      effectiveFrom: rule.effectiveFrom || '',
      effectiveTo: rule.effectiveTo || '',
      active: rule.active !== false
    });
  }, []);

  const handleDeactivateRule = useCallback(async (ruleId) => {
    if (!hasSelection || !canManageRules) return;
    clearFeedback();
    setRulesLoading(true);
    try {
      await ifrsService.deactivateCashflowRule({ companyId, ruleId });
      await loadRules();
      setMessage(L('Rule deactivated.', 'Kural pasiflestirildi.'));
    } catch (caught) {
      setError(caught.message || L('Rule could not be deactivated.', 'Kural pasiflestirilemedi.'));
      setRulesLoading(false);
    }
  }, [L, canManageRules, clearFeedback, companyId, hasSelection, loadRules]);

  const batchFacts = useMemo(() => {
    if (!activeBatch) return [];
    return [
      [L('Batch Version', 'İşlem Grubu Versiyonu'), activeBatch.batchVersion],
      [L('Batch ID', 'İşlem Grubu ID'), activeBatch.batchId],
      [L('Status', 'Durum'), mapStatus(activeBatch.status, isTr)],
      [L('Rule Set', 'Kural Seti'), activeBatch.ruleSetVersion],
      [L('Calc Engine', 'Hesaplama Motoru'), activeBatch.calculationEngineVersion],
      [L('Rows', 'Satir'), activeBatch.rowCount],
      [L('Source Hash', 'Kaynak Hash'), activeBatch.sourceSnapshotHash]
    ];
  }, [L, activeBatch, isTr]);

  return (
    <div className="ifrs-workspace">
      <PageHeader
        title={L('IFRS Analysis & Rule Management', 'IFRS Analiz ve Kural Yonetimi')}
        description={L('Approved batch inspection, report review, VUK-IFRS reconciliation, XBRL verification and cash flow rule management. Use the Transformation Wizard to run new batches.', 'Onaylı işlem grubu inceleme, rapor gözden geçirme, VUK-IFRS mutabakat, XBRL doğrulama ve nakit akış kural yönetimi. Yeni işlem grubu başlatmak için Dönüşüm Sihirbazı\'nı kullanın.')}
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      {message ? <div className="ifrs-banner ifrs-banner-success">{message}</div> : null}
      {error ? <div className="ifrs-banner ifrs-banner-error">{error}</div> : null}

      <section className="card ifrs-card">
        <div className="card-header"><div><h2>{L('Batch Inspection & Approval', 'İşlem Grubu İnceleme ve Onay')}</h2><p>{L('Inspect and approve an existing batch. Unapproved batches cannot be used as a report source. To run a new batch, use the Transformation Wizard.', 'Mevcut bir işlem grubunu incele ve onayla. Onaylanmayan işlem grubu rapor kaynağı olamaz. Yeni işlem grubu başlatmak için Dönüşüm Sihirbazı\'nı kullanın.')}</p></div></div>
        <div className="ifrs-inline-grid">
          <label>
            {L('Batch Version', 'İşlem Grubu Versiyonu')}
            <select value={selectedBatchId} onChange={(event) => setSelectedBatchId(event.target.value)} disabled={!hasSelection || batchLoading || batchOptions.length === 0}>
              <option value="">{L('Select batch version', 'İşlem grubu versiyonu seçin')}</option>
              {batchOptions.map((batch) => (
                <option key={batch.batchId} value={batch.batchId}>
                  {batch.batchVersion || `#${batch.batchId}`} {batch.status ? `(${mapStatus(batch.status, isTr)})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="ifrs-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/financial-statements/vuk-ifrs-wizard')}>{L('Go to Transformation Wizard', 'Donusum Sihirbazi\'na Git')}</button>
          <button type="button" className="btn-secondary" disabled={!hasSelection || !selectedBatchId || batchLoading} onClick={() => loadBatchById(selectedBatchId)}>{L('Get Batch', 'İşlem Grubunu Getir')}</button>
          <button type="button" className="btn-primary" disabled={!canWrite || !hasSelection || !activeBatch?.batchId || batchLoading} onClick={handleApproveBatch}>{L('Approve Batch', 'İşlem Grubunu Onayla')}</button>
        </div>
        {hasSelection && !batchLoading && batchOptions.length === 0 ? (
          <p className="muted">{L('No batch versions were found for the selected company and period.', 'Seçilen şirket ve dönem için işlem grubu versiyonu bulunamadı.')}</p>
        ) : null}
        {selectedBatchOption && !activeBatch ? (
          <p className="muted">
            {L('Selected', 'Seçilen')}: <strong>{selectedBatchOption.batchVersion || `#${selectedBatchOption.batchId}`}</strong>
          </p>
        ) : null}
        {batchFacts.length > 0 && <div className="ifrs-kv">{batchFacts.map(([key, value]) => <div key={key} className="ifrs-kv-row"><span>{key}</span><strong>{String(value ?? '-')}</strong></div>)}</div>}
      </section>

      <section className="card ifrs-card">
        <div className="card-header"><div><h2>{L('Reporting and Reconciliation', 'Raporlama ve Mutabakat')}</h2><p>{L('Trial balance, IFRS reports, reconciliation and XBRL endpoint check on approved batch.', 'Onaylı işlem grubu üzerinden mizan, IFRS raporları, mutabakat ve XBRL endpoint kontrolü.')}</p></div></div>
        <div className="ifrs-inline-grid"><label>{L('Report Type', 'Rapor Tipi')}<select value={reportType} onChange={(event) => setReportType(event.target.value)}>{reportOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label></div>
        <div className="ifrs-actions">
          <button type="button" className="btn-secondary" disabled={!hasSelection || reportLoading} onClick={handleLoadTrialBalance}>{L('Load Trial Balance', 'Trial Balance Yukle')}</button>
          <button type="button" className="btn-primary" disabled={!hasSelection || reportLoading} onClick={handleLoadReport}>{L('Load IFRS Report', 'IFRS Raporu Yukle')}</button>
          <button type="button" className="btn-secondary" disabled={!hasSelection || reportLoading} onClick={handleLoadAllReports}>{L('Load All IFRS Statements', 'Tum IFRS Tablolarini Yukle')}</button>
          <button type="button" className="btn-secondary" disabled={!hasSelection || reportLoading} onClick={handleLoadReconciliation}>{L('Load Reconciliation', 'Mutabakat Yukle')}</button>
          <button type="button" className="btn-secondary" disabled={!hasSelection || reportLoading} onClick={handleLoadXbrl}>{L('Check XBRL Endpoint', 'XBRL Endpoint Kontrol Et')}</button>
        </div>
        {reportResponse?.lines?.length > 0 && (
          <div className="ifrs-table-wrap">
            <h3>{reportOptions.find((o) => o.key === reportType)?.label || reportResponse.reportName} ({mapStatus(reportResponse.status, isTr)})</h3>
            <table>
              <thead>
                <tr>
                  <th>{L('Code', 'Kod')}</th>
                  <th>{L('Name', 'Ad')}</th>
                  <th>{L('Type', 'Tur')}</th>
                  <th className="ifrs-right">{L('Amount', 'Tutar')}</th>
                  <th>{L('Source Trace', 'Kaynak Iz')}</th>
                  <th>{L('Standard', 'Standart')}</th>
                </tr>
              </thead>
              <tbody>
                {reportResponse.lines.map((line) => {
                  const std = line.standardRef ? standardsByCode[line.standardRef] : null;
                  return (
                    <tr key={`${line.code}-${line.name}`}>
                      <td>{mapLineCode(line.code)}</td>
                      <td>{mapLineName(line.name)}</td>
                      <td>{mapAccountType(line.accountType)}</td>
                      <td className="ifrs-right">{line.amount}</td>
                      <td>{mapSourceTrace(line.sourceTrace)}</td>
                      <td>
                        {std
                          ? <a href={std.kgkUrl} target="_blank" rel="noopener noreferrer" className="ifrs-std-ref-badge" title={isTr ? std.titleTr : std.titleEn}>{line.standardRef}</a>
                          : (line.standardRef || '—')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {Object.keys(allReports).length > 0 && (
          <div className="ifrs-table-wrap">
            <h3>{L('IFRS Financial Statements (All)', 'IFRS Mali Tablolari (Tum)')}</h3>
            {reportOptions.map((opt) => {
              const item = allReports[opt.key];
              if (!item) return null;
              const lines = item.lines || [];
              const total = lines.reduce((acc, line) => acc + (Number(line.amount) || 0), 0);
              const sectionTotals = lines.reduce((acc, line) => {
                const key = String(line.accountType || 'UNKNOWN').toUpperCase();
                acc[key] = (acc[key] || 0) + (Number(line.amount) || 0);
                return acc;
              }, {});
              const sectionTotalText = Object.entries(sectionTotals)
                .map(([key, value]) => `${mapAccountType(key)}: ${formatAmount(value)}`)
                .join(' | ');
              return (
                <details key={`all-report-${opt.key}`} className="ifrs-report-details" open>
                  <summary>
                    {opt.label} | {L('Rows', 'Satir')}: {lines.length} | {L('Total', 'Toplam')}: {formatAmount(total)}
                  </summary>
                  {sectionTotalText ? (
                    <div className="ifrs-section-totals">{L('Section Totals', 'Bolum Toplamlari')}: {sectionTotalText}</div>
                  ) : null}
                  <table>
                    <thead>
                      <tr>
                        <th>{L('Code', 'Kod')}</th>
                        <th>{L('Name', 'Ad')}</th>
                        <th>{L('Type', 'Tur')}</th>
                        <th className="ifrs-right">{L('Amount', 'Tutar')}</th>
                        <th>{L('Source Trace', 'Kaynak Iz')}</th>
                        <th>{L('Standard', 'Standart')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => {
                        const std = line.standardRef ? standardsByCode[line.standardRef] : null;
                        return (
                          <tr key={`${opt.key}-${line.code}-${line.name}`}>
                            <td>{mapLineCode(line.code)}</td>
                            <td>{mapLineName(line.name)}</td>
                            <td>{mapAccountType(line.accountType)}</td>
                            <td className="ifrs-right">{formatAmount(line.amount)}</td>
                            <td>{mapSourceTrace(line.sourceTrace)}</td>
                            <td>
                              {std
                                ? <a href={std.kgkUrl} target="_blank" rel="noopener noreferrer" className="ifrs-std-ref-badge" title={isTr ? std.titleTr : std.titleEn}>{line.standardRef}</a>
                                : (line.standardRef || '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </details>
              );
            })}
          </div>
        )}
        {trialBalanceResponse?.lines?.length > 0 && <div className="ifrs-table-wrap"><h3>{L('IFRS Trial Balance', 'IFRS Mizan')}</h3><table><thead><tr><th>{L('Account', 'Hesap')}</th><th className="ifrs-right">{L('Debit', 'Borc')}</th><th className="ifrs-right">{L('Credit', 'Alacak')}</th><th className="ifrs-right">{L('Balance', 'Bakiye')}</th></tr></thead><tbody>{trialBalanceResponse.lines.map((line) => <tr key={line.accountCode}><td>{line.accountCode}</td><td className="ifrs-right">{line.debitTotal}</td><td className="ifrs-right">{line.creditTotal}</td><td className="ifrs-right">{line.balance}</td></tr>)}</tbody></table></div>}
        {reconciliationResponse && <div className="ifrs-kv"><div className="ifrs-kv-row"><span>{L('VUK Debit', 'VUK Borc')}</span><strong>{reconciliationResponse.vukDebitTotal}</strong></div><div className="ifrs-kv-row"><span>{L('VUK Credit', 'VUK Alacak')}</span><strong>{reconciliationResponse.vukCreditTotal}</strong></div><div className="ifrs-kv-row"><span>{L('IFRS Debit', 'IFRS Borc')}</span><strong>{reconciliationResponse.ifrsDebitTotal}</strong></div><div className="ifrs-kv-row"><span>{L('IFRS Credit', 'IFRS Alacak')}</span><strong>{reconciliationResponse.ifrsCreditTotal}</strong></div><div className="ifrs-kv-row"><span>{L('Debit Delta', 'Borc Farki')}</span><strong>{reconciliationResponse.debitDelta}</strong></div><div className="ifrs-kv-row"><span>{L('Credit Delta', 'Alacak Farki')}</span><strong>{reconciliationResponse.creditDelta}</strong></div><div className="ifrs-kv-row"><span>{L('Source Trace', 'Kaynak Iz')}</span><strong>{mapSourceTrace(reconciliationResponse.sourceTrace)}</strong></div></div>}
        {xbrlResponse && (
          <>
            <div className="ifrs-kv">
              <div className="ifrs-kv-row"><span>{L('Export Type', 'Export Tipi')}</span><strong>{xbrlResponse.exportType}</strong></div>
              <div className="ifrs-kv-row"><span>{L('Status', 'Durum')}</span><strong>{mapStatus(xbrlResponse.status, isTr)}</strong></div>
              <div className="ifrs-kv-row"><span>{L('Message', 'Mesaj')}</span><strong>{xbrlResponse.message}</strong></div>
              <div className="ifrs-kv-row"><span>{L('Source Trace', 'Kaynak Iz')}</span><strong>{mapSourceTrace(xbrlResponse.sourceTrace)}</strong></div>
              <div className="ifrs-kv-row"><span>{L('Fact Count', 'Fact Sayisi')}</span><strong>{xbrlResponse.factCount ?? 0}</strong></div>
              <div className="ifrs-kv-row"><span>{L('Missing Mandatory', 'Eksik Zorunlu')}</span><strong>{xbrlResponse.missingMandatoryCount ?? 0}</strong></div>
            </div>
            {Array.isArray(xbrlResponse.validationMessages) && xbrlResponse.validationMessages.length > 0 && (
              <div className="ifrs-table-wrap">
                <h3>{L('XBRL Validation Messages', 'XBRL Validasyon Mesajlari')}</h3>
                <ul className="placeholder-notes">
                  {xbrlResponse.validationMessages.map((item, idx) => (
                    <li key={`xbrl-validation-${idx}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <section className="card ifrs-card">
        <div className="card-header"><div><h2>{L('Cash Flow Classification Rules', 'Nakit Akis Siniflandirma Kurallari')}</h2><p>{L('operating / investing / financing + non-cash mapping rule management.', 'Nakit akis siniflandirmasi (isletme / yatirim / finansman) ve nakit-disi eslestirme kural yonetimi.')}</p></div></div>
        <div className="ifrs-actions">
          <label className="ifrs-checkbox"><input type="checkbox" checked={includeDefaults} onChange={(event) => setIncludeDefaults(event.target.checked)} />{L('include tenant defaults', 'tenant varsayilanlarini dahil et')}</label>
          <label className="ifrs-checkbox"><input type="checkbox" checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)} />{L('include inactive', 'pasif kurallari dahil et')}</label>
          <button type="button" className="btn-secondary" disabled={!hasSelection || rulesLoading} onClick={loadRules}>{L('Refresh Rules', 'Kurallari Yenile')}</button>
        </div>
        {canManageRules ? <div className="ifrs-rule-form"><label>{L('Account/Prefix', 'Hesap/Prefix')}<input value={ruleForm.accountCodeOrPrefix} onChange={(event) => setRuleForm((prev) => ({ ...prev, accountCodeOrPrefix: event.target.value }))} /></label><label>{L('Classification', 'Siniflandirma')}<select value={ruleForm.classification} onChange={(event) => setRuleForm((prev) => ({ ...prev, classification: event.target.value }))}><option value="OPERATING">{mapCashflowClassificationLabel('OPERATING')}</option><option value="INVESTING">{mapCashflowClassificationLabel('INVESTING')}</option><option value="FINANCING">{mapCashflowClassificationLabel('FINANCING')}</option></select></label><label>{L('Priority', 'Oncelik')}<input type="number" min="1" max="10000" value={ruleForm.rulePriority} onChange={(event) => setRuleForm((prev) => ({ ...prev, rulePriority: event.target.value }))} /></label><label>{L('Effective From', 'Gecerlilik Baslangici')}<input type="date" value={ruleForm.effectiveFrom} onChange={(event) => setRuleForm((prev) => ({ ...prev, effectiveFrom: event.target.value }))} /></label><label>{L('Effective To', 'Gecerlilik Bitisi')}<input type="date" value={ruleForm.effectiveTo} onChange={(event) => setRuleForm((prev) => ({ ...prev, effectiveTo: event.target.value }))} /></label><label className="ifrs-checkbox"><input type="checkbox" checked={ruleForm.nonCashFlag} onChange={(event) => setRuleForm((prev) => ({ ...prev, nonCashFlag: event.target.checked }))} />{L('non-cash', 'nakit-disi')}</label><label className="ifrs-checkbox"><input type="checkbox" checked={ruleForm.active} onChange={(event) => setRuleForm((prev) => ({ ...prev, active: event.target.checked }))} />{L('active', 'aktif')}</label><div className="ifrs-actions"><button type="button" className="btn-primary" disabled={!hasSelection || rulesLoading} onClick={handleSaveRule}>{editingRuleId ? L('Update Rule', 'Kurali Guncelle') : L('Create Rule', 'Kural Olustur')}</button><button type="button" className="btn-secondary" onClick={() => { setEditingRuleId(null); setRuleForm(defaultRuleForm); }}>{L('Clear Form', 'Formu Temizle')}</button></div></div> : <p className="muted">{L('This user role can only view rules.', 'Bu kullanici rolu sadece kural goruntuleme yetkisine sahip.')}</p>}
        {rules.length > 0 && <div className="ifrs-table-wrap"><table><thead><tr><th>ID</th><th>{L('Scope', 'Kapsam')}</th><th>{L('Account/Prefix', 'Hesap/Prefix')}</th><th>{L('Classification', 'Siniflandirma')}</th><th>{L('Non-Cash', 'Nakit-Disi')}</th><th>{L('Priority', 'Oncelik')}</th><th>{L('Effective', 'Gecerlilik')}</th><th>{L('Active', 'Aktif')}</th>{canManageRules ? <th>{L('Action', 'Islem')}</th> : null}</tr></thead><tbody>{rules.map((rule) => <tr key={rule.id}><td>{rule.id}</td><td>{isTr ? (rule.scopeType === 'TENANT_DEFAULT' ? 'TENANT VARSAYILAN' : rule.scopeType === 'COMPANY' ? 'SIRKET' : (rule.scopeType || '-')) : (rule.scopeType || '-')}</td><td>{rule.accountCodeOrPrefix}</td><td>{mapCashflowClassificationLabel(rule.classification)}</td><td>{rule.nonCashFlag ? L('YES', 'EVET') : L('NO', 'HAYIR')}</td><td>{rule.rulePriority}</td><td>{`${rule.effectiveFrom || '-'} -> ${rule.effectiveTo || '-'}`}</td><td>{rule.active ? L('YES', 'EVET') : L('NO', 'HAYIR')}</td>{canManageRules ? <td className="ifrs-table-actions"><button type="button" className="btn-secondary" onClick={() => handleEditRule(rule)}>{L('Edit', 'Duzenle')}</button>{rule.active ? <button type="button" className="btn-danger" onClick={() => handleDeactivateRule(rule.id)}>{L('Deactivate', 'Pasiflestir')}</button> : null}</td> : null}</tr>)}</tbody></table></div>}
      </section>
    </div>
  );
}

export default IfrsWorkspacePage;
